import { Injectable } from '@angular/core';
import { ChallengeHttpService } from '@core/http/challenge-http.service';
import { EventHttpService } from '@core/http/event-http.service';
import { OrganizationHttpService } from '@core/http/organization-http.service';
import { CentralConfigService } from '@core/services/central-config.service';
import { GlobalFilterStoredService } from '@core/services/global-filter-stored.service';
import {
  FilterCriteria,
  SearchEntitiesService,
} from '@core/services/search-entities.service';
import { UserConfigManagementService } from '@core/services/user-config-management.service';
import { maxPageSize } from '@shared/constants/common';
import { EntityName } from '@shared/enums/entity-name.enum';
import { GlobalEvent } from '@shared/enums/event-bus.enum';
import { StorageEnum } from '@shared/enums/storage.enum';
import { UrlParam } from '@shared/enums/url-param.enum';
import { ChannelFilter } from '@shared/interfaces/organization.interface';
import { StringUtils } from '@shared/utils/string-utils';
import { EventBusService } from 'ngx-eventbus';
import { BehaviorSubject, Observable } from 'rxjs';
@Injectable({
  providedIn: 'root',
})
export class SearchChannelsService {
  filterDto!: FilterCriteria;
  keyword$ = new BehaviorSubject<string>(null);
  historyChannels$ = new BehaviorSubject([]);
  filterToken: number;
  activeFilter$ = new BehaviorSubject(null);

  constructor(
    private readonly searchEntitiesService: SearchEntitiesService,
    private readonly orgService: OrganizationHttpService,
    private readonly eventService: EventHttpService,
    private readonly filterStoredService: GlobalFilterStoredService,
    private readonly userConfig: UserConfigManagementService,
    private readonly centralConfig: CentralConfigService,
    private readonly eventBus: EventBusService,
    private readonly challengesService: ChallengeHttpService
  ) {
    this.filterDto = {
      ...this.searchEntitiesService.filterDTO,
      organizationIds: [this.centralConfig.innovationSpaceId],
      pageSize: maxPageSize,
    };

    this.keyword$.subscribe(async keyword => {
      this.filterDto = await this.getFilterDto(keyword);
    });

    this.fetchAndEmitRecentChannels();

    this.filterStoredService.getCurrentFilterToken().subscribe(async token => {
      if (token) {
        this.filterToken = token;
      }
    });
  }

  getKeyword(): Observable<string> {
    return this.keyword$.asObservable();
  }

  getEntitesAPIs = {
    [EntityName.Event]: dto => this.eventService.paginateX(dto),
    [EntityName.Organization]: dto => {
      return this.orgService.paginateAsPost(
        dto,
        null,
        dto.organizationId && {
          organizationId: +dto.organizationId,
        }
      );
    },
    [EntityName.Challenge]: dto => this.challengesService.paginateX(dto),
  };

  async getFilterDto(keyword: string): Promise<FilterCriteria> {
    const filterCriteria = await this.filterStoredService.getFilterCriteria();
    const dto = {
      ...this.filterDto,
      ...filterCriteria,
      keyword,
    };
    return dto;
  }

  handleKeywordChange(keyword: string) {
    this.keyword$.next(keyword);
  }

  async getRecentChannels() {
    return await this.userConfig.get(StorageEnum.recentChannels);
  }

  async storeChannelSelection(channel) {
    const recentChannels =
      (await this.userConfig.get(StorageEnum.recentChannels)) || [];
    const channels = [...recentChannels];
    const isExisted = channels.find(
      item => item.id === channel.id && item.entityName === channel.entityName
    );

    this.searchEntitiesService.setChannelFilter({
      threadId: channel.id,
      threadEntity:
        channel.entityName === EntityName.Event ? EntityName.Event : '',
    });

    await this.setActiveChannel(channel);

    if (isExisted) return;
    channels.push(channel);
    this.userConfig.set(StorageEnum.recentChannels, channels);
    this.historyChannels$.next(channels);
  }

  async deleteChannel(channel) {
    const recentChannels =
      (await this.userConfig.get(StorageEnum.recentChannels)) || [];

    const newChannels = recentChannels.filter(
      item =>
        !(item.id === channel.id && item.entityName === channel.entityName)
    );

    this.userConfig.set(StorageEnum.recentChannels, newChannels);
    this.historyChannels$.next(newChannels);
  }

  async setActiveChannel(channel) {
    await this.userConfig.set(StorageEnum.activeFilterChannel, channel);
  }

  async getActiveChannel() {
    const activeChannel = await this.userConfig.get(
      StorageEnum.activeFilterChannel
    );

    return activeChannel;
  }

  async onSelectChannel(channel) {
    const threadEntity =
      channel.entityName === EntityName.Event ? EntityName.Event : '';
    const { ...restDto } = this.searchEntitiesService.filterDTO;
    const globalFilter = await this.userConfig.get(StorageEnum.globalFilters);
    const orgId = this.centralConfig.innovationSpaceId || this.filterToken;
    let keyword = '';

    if (globalFilter) {
      keyword = globalFilter.keyword;
    }

    this.eventBus.triggerEvent(GlobalEvent.Filter, {
      filterCriteria: {
        ...restDto,
        ...(orgId && {
          organizationIds: [orgId],
          threadId: channel.id,
          threadEntity,
        }),
        keyword,
      },
    });

    await this.storeChannelSelection(channel);
    await this.getRecentChannels();

    this.keyword$.next('');
    this.setSubCommunityParam(channel);
  }

  async resetChannel(): Promise<void> {
    const filterCriteria = await this.filterStoredService.getFilterCriteria();
    const { keyword, filteringPath, organizationIds } = filterCriteria;
    const orgId = this.centralConfig.innovationSpaceId || this.filterToken;

    this.searchEntitiesService.setChannelFilter({});

    this.eventBus.triggerEvent(GlobalEvent.Filter, {
      filterCriteria: {
        ...filterCriteria,
        organizationIds: orgId ? [orgId] : [...organizationIds],
        organizationId: orgId || organizationIds[0],
        keyword,
        threadId: '',
        threadEntity: '',
      },
    });

    await this.filterStoredService.saveFilterCriteria(
      keyword,
      filteringPath,
      organizationIds[0],
      null,
      ''
    );

    await this.setActiveChannel(null);

    this.filterStoredService.setTopicFilter(null, '');
    StringUtils.removeParamFromUrl(UrlParam.SubOrganizationId);
  }

  setSubCommunityParam(channel: ChannelFilter): void {
    const subOrgId = StringUtils.getParamFromUrl(UrlParam.SubOrganizationId);

    if (
      channel.entityName === EntityName.Organization &&
      subOrgId !== channel.id
    ) {
      StringUtils.setQueryParamOnUrl(
        UrlParam.SubOrganizationId,
        channel.id.toString(),
        null,
        true
      );
    }
  }

  private fetchAndEmitRecentChannels(): void {
    this.getRecentChannels().then(recentChannels => {
      this.historyChannels$.next(recentChannels || []);
    });
  }
}
